import React, { useState, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components';
import Chevron from './icons/Chevron';
import { Color, Colors } from '../styles/Colors';
import { CalendarButton } from '../styles/Buttons';
import CalendarIcon from './icons/CalendarIcon';
import { DateBtnContainer } from '../styles/Buttons';

const Wrapper = styled.div`
    position: relative;
`;

const Calendar = styled.div`
    background-color: transparent; 
    color: ${Color(Colors.white)}; 
    position: absolute;
    right: 0;
    left: auto;
    top: 40px; 
    z-index: 999;
    max-width: 315px;   
    width: 100%; 
    
    & > div {
        margin-top: 8px; 
        border-radius: 4px; 
        background-color: ${Color(Colors.blue)}; 
        height: ${({$isVisible}) => $isVisible ? '372px' : '0'}; 
        visibility: ${({$isVisible}) => $isVisible ? 'visible' : 'hidden'};
        box-shadow: ${({$isVisible}) => $isVisible ? '8px 8px 8px rgba(0, 0, 0, 0.3)' : '0'}; 
        opacity: ${({$isVisible}) => $isVisible ? '1' : '0'}; 
        transition:  height 0.5s ease-out, opacity 0.5s ease-out, box-shadow 0.5s ease-out, visibility 0.5s ease-out;

        // Month picker wrapper
        & > div:first-of-type {
            display: flex; 
            align-items: center;
            justify-content: space-between; 
            padding: 1rem; 
            border-radius: 4px 4px 0 0; 
            margin-top: 0.5rem; 
        }
    
        // Wrapper(s) for the days and dates
        & > div:nth-of-type(2),
        & > div:last-of-type {
            display: grid; 
            grid-template-columns: repeat(7, 1fr);   
            justify-items: center;
            align-items: center;
            justify-content: center;
            align-content: center;
            opacity: ${({$isVisible}) => $isVisible ? 1 : 0}; 
            visibility: ${({$isVisible}) => $isVisible ? 'visible' : 'hidden'};
            transition: ${({$isVisible}) => $isVisible === true && 'opacity 0.5s 0.3s ease-out, visibility 0.5s 0.3s ease-out'};
            transition: ${({$isVisible}) => $isVisible === false && 'opacity 0.2s ease-out, visibility 0.0s ease-out'}; 
        }
        
        // Dates wrapper
        & > div:last-of-type {
            padding: 1rem; 
            border-radius: 0 0 4px 4px; 
        }
    
        // Buttons for the dates
        & > div:last-of-type > button {
            display: flex;
            justify-content: center;
            align-items: center; 
            border: none; 
            background-color: ${Color(Colors.lightBlue)}; 
            color: ${Color(Colors.white)};
            padding: 0.5rem; 
            height: 28px;
            width: 28px;
            margin: 0.5rem;
        }
    }

`;

const DateComponent = ({usersDate, setUsersDate, setError, tool, usersDateKey }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const [currentYear, setCurrentYear] = useState(() => {
        const saveCurrYear = localStorage.getItem('saveCurrYear_1');
        return saveCurrYear ? parseInt(saveCurrYear, 10) : new Date().getFullYear();
    });

    const [currentMonth, setCurrentMonth] = useState(() => {
        const saveCurrMonth = localStorage.getItem('saveCurrMonth_1');
        return saveCurrMonth ? parseInt(saveCurrMonth, 10) : new Date().getMonth();
    });

    const today = new Date();
    const thisYear = today.getFullYear();
    const thisMonth = today.getMonth();
    const modalRef = useRef();

    useEffect(() => {
        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isVisible])

    useEffect(() => {
        if (!usersDate) return;

        const selectedDate = new Date(usersDate); 
        if (Number.isNaN(selectedDate.getTime())) return;

        setCurrentYear(selectedDate.getFullYear());
        setCurrentMonth(selectedDate.getMonth());
    }, [usersDate]); 

    useEffect(() => {
        localStorage.setItem('saveCurrYear_1', currentYear);
        localStorage.setItem('saveCurrMonth_1', currentMonth);
        localStorage.setItem(usersDateKey, usersDate);
    }, [currentYear, currentMonth, usersDate]);

    const getFirstAndLastDays = (year, month) => {
        let firstDay = new Date(year, month, 1).getDay();
        firstDay = (firstDay === 0) ? 6 : firstDay - 1;
        const lastDay = new Date(year, month + 1, 0).getDate();

        return { firstDay, lastDay };
    };

    const generateDays = (year, month) => {
        const { lastDay, firstDay } = getFirstAndLastDays(year, month);
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<button key={`empty-${i}`} disabled style={{ backgroundColor: 'transparent' }}></button>);
        }

        for (let day = 1; day <= lastDay; day++) {
            const dateOfDay = new Date(year, month, day).toLocaleDateString('sv-SE');
            const isUserDate = usersDate === dateOfDay;
            days.push(
                <button
                    onClick={() => handleUserDate(dateOfDay)}
                    style={{
                        borderRadius: '6px',
                        border: isUserDate ? '2px solid #ff0000' : 'none',
                    }}
                    key={day}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    const getYearInTheFuture = (year) => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + year);

        return date.getFullYear();
    }

    const getYearInThePast = (year) => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - year);

        return date.getFullYear();
    }
    
    const handlePrevMonth = useCallback(() => {
        const yearInThePastAG = getYearInThePast(2);
        const yearInThePastRG = getYearInThePast(1);

        if (tool === 'agendaGenerator' && (currentYear === yearInThePastAG && currentMonth === thisMonth)) return;
        if (tool === 'reportGenerator' && (currentYear === yearInThePastRG && currentMonth === thisMonth)) return;

        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    }, [currentYear, currentMonth]);

    const handleNextMonth = useCallback(() => {
        const yearsInTheFuture = getYearInTheFuture(1); 
        if (tool === 'agendaGenerator' && (currentYear === thisYear && currentMonth === thisMonth)) return;
        if (tool === 'reportGenerator' && (currentYear === yearsInTheFuture && currentMonth === thisMonth)) return;

        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(y => y + 1);
        } else {
            setCurrentMonth(m => m + 1);
        }
    }, [tool, currentYear, currentMonth, thisYear, thisMonth]);


    const handleUserDate = (dateOfDay) => {
        setUsersDate(dateOfDay);
        localStorage.setItem(usersDateKey, dateOfDay);

        if (dateOfDay) {
            setError(prevError => ({ ...prevError, lastMeeting: '', dopDate: '', dowDateTo: '', dowDateFrom: '' }))
        }
    };

    const toggleCalendar = () => {
        setIsVisible(!isVisible)
    }

    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            setIsVisible(!isVisible)
        }
    }

    const handleMouseEnter = () => {
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        setIsHovered(false)
    }


    const calendarButtons = () => {
        const yearInThePastAG = getYearInThePast(2);
        const yearInThePastRG = getYearInThePast(1);
        const yearInTheFutureRG = getYearInTheFuture(1);

        const isPast = 
            tool === 'agendaGenerator'
                ? (yearInThePastAG === currentYear) && (currentMonth === thisMonth)
                : (yearInThePastRG === currentYear) && (currentMonth === thisMonth)

        const isToday =
            tool === 'agendaGenerator'
                ? (currentYear === thisYear && currentMonth === thisMonth)
                : (currentYear === yearInTheFutureRG && currentMonth === thisMonth)

        return(
            <DateBtnContainer $isPast={isPast} $isToday={isToday}>
                <button onClick={handlePrevMonth}>
                    <Chevron />
                </button>
                <span>{new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' })} {currentYear}</span>
                <button onClick={handleNextMonth}>
                    <Chevron />
                </button>
            </DateBtnContainer>
        )

    }

    return (
        <Wrapper ref={modalRef}>
            <CalendarButton
                $isVisible={isVisible}
                $isHovered={isHovered}
                onClick={toggleCalendar}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <CalendarIcon />
                {usersDate
                    ? new Date(usersDate).toLocaleDateString('sv-SE')
                    : 'YYYY-MM-DD'
                }
            </CalendarButton>
            <Calendar 
                $isVisible={isVisible}
                $tool={tool}
            >
                <div>
                    {calendarButtons()}
                    <div>
                        <div>Mån</div>
                        <div>Tis</div>
                        <div>Ons</div>
                        <div>Tors</div>
                        <div>Fre</div>
                        <div>Lör</div>
                        <div>Sön</div>
                    </div>
                    <div>
                        {generateDays(currentYear, currentMonth)}
                    </div>
                </div>
            </Calendar>
        </Wrapper>
    );
}

export default DateComponent